import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom'; // Import Link and useLocation from React Router
import { UserRoleContext } from '../context/UserRoleContext';
import { EventContext } from '../context/EventContext';
import { useAuth } from '../context/AuthContext';
import { Role, ROLES } from '../constants/roles';
import { ROUTES } from '../constants/routes';
import { getIcon } from '../services/iconService';

interface EventMenuBarProps {
  closeSideBar: () => void;
}

const SideBar: React.FC<EventMenuBarProps> = ({ closeSideBar }) => {
  const { currentEvent, setCurrentEvent } = useContext(EventContext);  // Assuming the event data is in context
  const { userRole, setUserRole } = useContext(UserRoleContext);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null); // State to track selected category
  const location = useLocation(); // Use this to track the current location (active route)

  const toggleCategory = (category: string) => {
    setSelectedCategory((prevCategory) =>
      prevCategory === category ? null : category // Toggle category visibility
    );
  };

  const isActive = (path: string) => location.pathname === path; // Check if the path is active
  const lowEventRolesWithAccess: Role[] = [ROLES.ADMIN, ROLES.JUDGE, ROLES.MANAGER];
  const eventAdminAccess = currentEvent && userRole?.host_id === currentEvent?.host_id && userRole.role_id === ROLES.ADMIN;
  const eventLowAdminAccess = currentEvent && userRole?.host_id === currentEvent?.host_id && lowEventRolesWithAccess.includes(userRole.role_id as Role);


  return (
    <div style={styles.sidebar}>
      <ul style={styles.menuList}>
        {/* Events Category */}
        {true && <li>
          <button
            onClick={() => toggleCategory('events')}
            style={selectedCategory === 'events' ? { ...styles.categoryButton, ...styles.selectedCategoryButton } : styles.categoryButton}
          >
          <img style={styles.categoryButtonIcon} src={getIcon('menu-events')} alt={'events'}></img>
            Events
          </button>
          {selectedCategory === 'events' && (
            <ul style={styles.subMenu}>
              {currentEvent && <li>
                <Link
                  to={ROUTES.EVENT_OVERVIEW}
                  style={(isActive(ROUTES.EVENT_OVERVIEW) || isActive(ROUTES.EVENT_LOGO)) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  Event Overview
                </Link>
              </li>}

                <li>
                  <Link
                    to={ROUTES.CREATE_REGISTRANT}
                    style={isActive(ROUTES.CREATE_REGISTRANT) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Cyne Up For an Event
                  </Link>
                </li>
              {(currentEvent && (currentEvent.host_id == userRole?.host_id || userRole?.organization_id)) && (
                <li>
                  <Link
                    to={ROUTES.REGISTRANTS_LIST}
                    style={isActive(ROUTES.REGISTRANTS_LIST) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    View Event Registrants
                  </Link>
                </li>
              )}
              {/* {eventAdminAccess && (
                <li>
                  <Link
                    to="/sort-registrants"
                    style={isActive('/sort-registrants') ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Sort Registrants
                  </Link>
                </li>
              )} */}
              {!!(eventLowAdminAccess) && (
                <li>
                  <Link
                    to={ROUTES.GROUPS}
                    style={isActive(ROUTES.GROUPS) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    {currentEvent?.event_type_group_name_plural ?? 'Groups'}
                  </Link>
                </li>
              )}

              {!!(eventLowAdminAccess && currentEvent.has_judging) && (
                <li>
                  <Link
                    to={ROUTES.SCORECARDS}
                    style={isActive(ROUTES.SCORECARDS) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Edit Scorecards
                  </Link>
                </li>
              )}

              {currentEvent && (
                <li>
                  <Link
                    to={ROUTES.MESSAGES}
                    style={isActive(ROUTES.MESSAGES) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    {`${eventLowAdminAccess ? 'Send' : 'View'} Event Messages`}
                  </Link>
                </li>
              )}

              {(eventLowAdminAccess == true && (currentEvent?.checkin_open == true || currentEvent?.registration_open == true)) && (
                <li>
                  <Link
                    to={ROUTES.QR_CODES}
                    style={isActive(ROUTES.QR_CODES) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Event QR Codes
                  </Link>
                </li>
              )}
              {eventLowAdminAccess && currentEvent?.event_type_id === 'MUSIC' && (
                <li>
                  <Link
                    to={ROUTES.EDIT_EVENT_INSTRUMENTS}
                    style={isActive(ROUTES.EDIT_EVENT_INSTRUMENTS) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Edit Event Instruments
                  </Link>
                </li>
              )}

              {eventAdminAccess && (
                <li>
                  <Link
                    to={ROUTES.EDIT_EVENT_QUESTIONS}
                    style={isActive(ROUTES.EDIT_EVENT_QUESTIONS) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Edit Event Questions
                  </Link>
                </li>
              )}
              {userRole?.host_id && userRole.role_id == 'ADMIN' && (
                <li>
                  <Link
                    to={ROUTES.CREATE_EVENT}
                    style={isActive(ROUTES.CREATE_EVENT) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                  >
                    Create New Event
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to={ROUTES.SWITCH_EVENTS}
                  style={isActive(ROUTES.SWITCH_EVENTS) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  Switch Current Event
                </Link>
              </li>
            </ul>
          )}
        </li>}

        {/* Organization Category */}
        {(userRole) && <li>
          <button
            onClick={() => toggleCategory('organization')}
            style={selectedCategory === 'organization' ? { ...styles.categoryButton, ...styles.selectedCategoryButton } : styles.categoryButton}
          >
          <img style={styles.categoryButtonIcon} src={getIcon('menu-hosts')} alt={'host-info'}></img>
             {userRole?.organization_id ? 'Organization Info' : 'Host Info'}
          </button>
          {selectedCategory === 'organization' && (
            <ul style={styles.subMenu}>
              {userRole?.host_id && <li>
                <Link
                  to={ROUTES.HOST_OVERVIEW}
                  style={isActive(ROUTES.HOST_OVERVIEW) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  Host Overview
                </Link>
              </li>}
              {userRole?.organization_id && <li>
                <Link
                  to={ROUTES.ORGANIZATION_OVERVIEW}
                  style={isActive(ROUTES.ORGANIZATION_OVERVIEW) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  Organization Overview
                </Link>
              </li>}

              {userRole?.organization_id && currentEvent &&<li>
                <Link
                  to={ROUTES.REGISTRANTS_LIST}
                  style={isActive(ROUTES.REGISTRANTS_LIST) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  View Registrants
                </Link>
              </li>}
              {(userRole?.organization_id || userRole?.host_id) && <li>
                <Link
                  to="/roles"
                  style={isActive('/roles') ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
                >
                  Assign Roles
                </Link>
              </li>}
              { userRole?.host_id != '' && <Link
                to="/host-org-overview"
                style={isActive(ROUTES.HOST_ORG_OVERVIEW) ? { ...styles.menuItem, ...styles.selectedMenuItem } : styles.menuItem}
              >Create New Host
              </Link>}
            </ul>
          )}
        </li>}

        {(userRole?.role_id == ROLES.ADMIN || userRole?.role_id == ROLES.BILLING) && (userRole?.host_id && userRole?.host_id != "") && <li>
          <button
            style={isActive(ROUTES.BILLING) ? { ...styles.categoryButton, ...styles.selectedCategoryButton } : styles.categoryButton}
            onClick={() => navigate(ROUTES.BILLING)}
          >
            <img style={styles.categoryButtonIcon} src={getIcon('menu-billing')} alt={'billing'}></img>
            Billing
          </button>
        </li>}

        <li>
          <button
            onClick={() => { signOut(); setUserRole(null); setCurrentEvent(null); closeSideBar(); }}
            style={styles.categoryButton}
          >
          <img style={styles.categoryButtonIcon} src={getIcon('menu-logout')} alt={'logout'}></img>
            Logout
          </button>
        </li>
      </ul>
    </div>
  );
};

// Styles for the menu
const styles = {
  sidebar: {
    width: '200px', // Width of the sidebar
    backgroundColor: '#FAFAFA', //'#2c3e50',
    color: '#fff',
    height: 'calc(100vh - 50px)', // Full height of the screen
    padding: '10px',
    position: 'fixed' as 'fixed',
    top: '50px', // Adjust this based on your navbar height
    left: 0,
    zIndex: 10, // Ensure sidebar is not below other elements
    boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.2)'
  },
  menuList: {
    listStyle: 'none',
    padding: 0,
    height: 'calc(100vh - 50px)',
    overflow: 'auto'

  },
  categoryButton: {
    color: '#000',//'#fff',
    fontSize: '20px',
    padding: '6px',
    display: 'flex',
    marginBottom: '10px',
    borderRadius: '4px',
    backgroundColor: '#FAFAFA',//'#34495e',
    width: '100%',
    cursor: 'pointer',
    // fontWeight: 'bold',
    textAlignment: 'left',
    alignItems: 'center',
  },
  selectedCategoryButton: {
    backgroundColor: '#51BCE4',//'#51BCE4', // Highlight color for selected tab
    fontWeight: 'bold',
    color: '#fff', // Make the selected tab text bold
        textAlignment: 'left'

  },
  subMenu: {
    // marginLeft: '20px', // Indent the sub-menu items
    listStyle: 'none',
    padding: 0,
  },
  menuItem: {
    color: '#000',//'#fff',
    textDecoration: 'none',
    padding: '5px',
    display: 'block',
    marginBottom: '10px',
    borderRadius: '4px',
    backgroundColor: '#FAFAFA',//'#34495e',
    width: '90%',
    cursor: 'pointer',
    textAlignment: 'left'
  },
  selectedMenuItem: {
    backgroundColor: '#51BCE4',//'#51BCE4', // Highlight color for selected tab
    fontWeight: 'bold', // Make the selected tab text bold
    color: 'white'
  },
  categoryButtonIcon: {
    width: '35px',
    height: '35px'
  }
};

export default SideBar;
